// src/Pages/Home.jsx
import React, { useState } from "react";
import {
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  Container,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContactDialog from "./ContactDialog";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [contactOpen, setContactOpen] = useState(false);

  const handleOpenContact = () => {
    setContactOpen(true);
  };

  const handleCloseContact = () => {
    setContactOpen(false);
  };

  return (
    <>
      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ background: "linear-gradient(to right, #22c946ff, #a3e635)" }}
      >
        <Toolbar sx={{ justifyContent: "center", gap: 2 }}>
          <Box
            component="img"
            src="/images/logo.jpg"
            alt="Zgiri Robotics Logo"
            sx={{
              height: 40,
              width: 40,
              borderRadius: 1,
              objectFit: "contain",
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "black" }}>
            ZGIRI ROBOTICS
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          mt: "64px",
          height: { xs: "70vh", md: "90vh" },
          width: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          backgroundImage: "url(/images/img.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Dark gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.1))",
            backdropFilter: "blur(2px)",
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            color: "white",
            pl: { xs: 3, md: 8 },
            pr: { xs: 3, md: 8 },
            animation: "fadeIn 2s ease-in-out",
          }}
        >
          <Typography
            variant={isMobile ? "h4" : "h2"}
            fontWeight="bold"
            gutterBottom
          >
            Welcome to ZGIRI ROBOTICS
          </Typography>
          <Typography variant="h6" paragraph>
            Driving Robotics and Digital Transformation
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleOpenContact}
            sx={{
              mt: 2,
              backgroundColor: "#22c946",
              color: "black",
              fontWeight: "bold",
              borderRadius: 4,
              px: 4,
              py: 1.5,
              textTransform: "uppercase",
              "&:hover": {
                backgroundColor: "#1a9b38",
              },
            }}
          >
            Contact Us
          </Button>
        </Box>
      </Box>

      {/* About Us */}
      <Divider />
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Our Mission
        </Typography>
        <Typography variant="body1" paragraph>
          To equip robotics enthusiasts, entrepreneurs, and industry leaders
          with cutting-edge robotics and digital transformation solutions,
          enabling them to increase efficiency, productivity, and global
          competitiveness.
        </Typography>
        <Typography variant="body1" paragraph>
          Whether you are a student eager to master robotics, an entrepreneur
          looking to scale with digital automation, or an industry leader ready
          to push technological boundaries, we bring expertise, adaptability,
          and strategic vision to turn ideas into reality.
        </Typography>
        <Typography variant="body1" fontWeight="medium">
          Welcome to Zgiri Robotics, where innovation meets resilience.
        </Typography>
      </Container>

      {/* What We Do */}
      <Divider />
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          What We Do
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Empowering the Next Generation
          </Typography>
          <Typography variant="body1" paragraph>
            We provide industrial robotics and digitalization training to
            students at tertiary-level technical colleges and undergraduates,
            preparing them to integrate into industry with confidence.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Supporting Small and Medium Enterprises (SMEs)
          </Typography>
          <Typography variant="body1" paragraph>
            We enable SMEs to adopt robotic and advanced technological
            solutions, enhancing safety, quality, and productivity for
            sustainable growth.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Transforming Large-Scale Industries
          </Typography>
          <Typography variant="body1" paragraph>
            For corporations, we deliver tailored robotics and digitalization
            solutions to boost operational efficiency and ensure competitiveness
            in a digital world.
          </Typography>
        </Box>
      </Container>

      {/* Our Identity */}
      <Divider />
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Our Identity
        </Typography>
        <Typography variant="body1" paragraph>
          At Zgiri Robotics, we pride ourselves on blending world-class
          expertise with authentic Sri Lankan values.
        </Typography>
        <Typography variant="body1" paragraph>
          Our team includes engineers trained by global manufacturers and
          integrators. We’re backed by support from leading robot manufacturers
          and service centers, ensuring clients receive customized and reliable
          solutions.
        </Typography>
        <Typography variant="body1" paragraph>
          We align our educational initiatives with globally recognized course
          content, ensuring students and professionals remain competitive in a
          fast-evolving tech landscape.
        </Typography>
        <Typography variant="body1" paragraph>
          With Zgiri Robotics, you’ll find a partner that combines cutting-edge
          technology with human-centric values — driving transformation across
          industries and communities.
        </Typography>
      </Container>

      {/* Footer */}
      <Box sx={{ backgroundColor: "#000", color: "#fff", mt: 8, pt: 6, pb: 4 }}>
        <Container>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {/* Logo & About */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                component="img"
                src="/images/logo.jpg"
                alt="Zgiri Robotics Logo"
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  objectFit: "cover",
                  backgroundColor: "#fff",
                }}
              />
              <Box>
                <Typography variant="subtitle1">ZGIRI ROBOTICS</Typography>
              </Box>
            </Box>

            {/* Contact Info with icons */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2">
                  ZGIRI ROBOTICS (PVT) LTD, No 89, Zone - 2, Millennium City,
                  Ekala, Ja-Ela.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" />
                <Typography variant="body2">+94 78 204 3579</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon fontSize="small" />
                <Typography variant="body2">robotics@zgiri.com</Typography>
              </Box>
            </Box>

            {/* Social Links */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Follow Us:
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <a
                  href="https://linkedin.com/company/zgiri-robotics"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff" }}
                >
                  <LinkedInIcon fontSize="large" />
                </a>
              </Box>
            </Box>
          </Box>

          {/* Centered Copyright */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="gray">
               ZGIRI ROBOTICS (PVT) LTD © 2025
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Dialog */}
      <ContactDialog open={contactOpen} handleClose={handleCloseContact} />

      {/* Keyframes animation */}
      <style>{`
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default Home;
